import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { HiPlay } from 'react-icons/hi2';
import ReactPlayer from 'react-player';
import axios from 'axios';

const API = 'https://us-central1-inprojuy-f1173.cloudfunctions.net/app';

const Wrap = styled.section`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 0 10px;
`;

const Content = styled.div`
  width: min(1280px, calc(100% - 36px));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.8rem);
  line-height: 1;
  color: #2f3640;
  font-weight: 800;
`;

const Slider = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 54px;

  @media (max-width: 768px) {
    padding: 0 36px;
  }
`;

const Arrow = styled.button`
  position: absolute;
  top: 44%;
  transform: translateY(-50%);
  width: 64px;
  height: 64px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
  z-index: 3;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.85);
  }

  span {
    font-size: 38px;
    line-height: 1;
    font-weight: 700;
    margin-top: -5px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const Prev = styled(Arrow)`left: -8px;`;
const Next = styled(Arrow)`right: -8px;`;

const Viewport = styled.div`
  width: 100%;
  overflow: hidden;
`;

const Track = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.count}, minmax(0, 1fr));
  gap: 18px;
`;

const Card = styled.article`
  min-width: 0;
  cursor: pointer;
`;

const ThumbWrap = styled.div`
  position: relative;
  width: 100%;
`;

const Thumb = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 18px;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const Play = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  svg {
    width: 56px;
    height: 56px;
    color: #fff;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
  }
`;

const VideoTitle = styled.h3`
  margin: 10px 0 2px;
  color: #2f3640;
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1.05;
`;

const VideoDate = styled.p`
  margin: 0;
  color: #6a7078;
  font-size: 1.05rem;
  font-weight: 500;
`;

/* ── Modal ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 2.2rem;
  line-height: 1;
  cursor: pointer;
  z-index: 10;
  &:hover { color: #ccc; }
`;

const SectionVideos = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [startIndex, setStartIndex] = useState(0);
  const [videos, setVideos] = useState([]);
  const [modalVideo, setModalVideo] = useState(null); // video seleccionado para el modal

  useEffect(() => {
    axios.get(`${API}/videos`)
      .then((res) => { if (Array.isArray(res.data) && res.data.length > 0) setVideos(res.data); })
      .catch((err) => console.error('Error al cargar videos:', err));
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cerrar modal con Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setModalVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const visible = isMobile ? 1 : 3;
  const maxStart = Math.max(0, videos.length - visible);
  const shown = videos.slice(startIndex, startIndex + visible);

  useEffect(() => {
    if (videos.length <= visible || modalVideo) return;
    const interval = setInterval(() => {
      setStartIndex((prev) => (prev >= maxStart ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [visible, maxStart, videos, modalVideo]);

  if (videos.length === 0) return null;

  return (
    <>
      <Wrap>
        <Content>
          <Header>
            <Title>Videos</Title>
          </Header>
          <Slider>
            <Prev onClick={() => setStartIndex((prev) => (prev <= 0 ? maxStart : prev - 1))} aria-label="Videos anteriores">
              <span aria-hidden>‹</span>
            </Prev>
            <Viewport>
              <Track count={visible}>
                {shown.map((video) => (
                  <Card key={video.docId} onClick={() => setModalVideo(video)}>
                    <ThumbWrap>
                      <Thumb src={video.thumbnail || ''} alt={video.titulo} />
                      <Play aria-hidden>
                        <HiPlay />
                      </Play>
                    </ThumbWrap>
                    <VideoTitle>{video.titulo}</VideoTitle>
                    <VideoDate>{video.fechacarga}</VideoDate>
                  </Card>
                ))}
              </Track>
            </Viewport>
            <Next onClick={() => setStartIndex((prev) => (prev >= maxStart ? 0 : prev + 1))} aria-label="Videos siguientes">
              <span aria-hidden>›</span>
            </Next>
          </Slider>
        </Content>
      </Wrap>

      {/* Modal de reproducción */}
      {modalVideo && (
        <Overlay onClick={() => setModalVideo(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <CloseBtn onClick={() => setModalVideo(null)} aria-label="Cerrar">✕</CloseBtn>
            <ReactPlayer
              url={modalVideo.url}
              width="100%"
              height="100%"
              style={{ aspectRatio: '16/9' }}
              controls
              playing
            />
          </ModalBox>
        </Overlay>
      )}
    </>
  );
};

export default SectionVideos;
